import React from 'react';
import { Check, Undo2 } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import { Button } from './ui/button';

type CommentThreadActionButtonProps = {
  onClick: () => void;
  tooltipText: string;
  ariaLabel?: string;
  variant: 'resolve' | 'unresolve';
  disabled?: boolean;
  label?: string;
};

export const CommentThreadActionButton: React.FC<CommentThreadActionButtonProps> = ({
  onClick,
  tooltipText,
  ariaLabel,
  variant,
  disabled = false,
  label,
}) => {
  const Icon = variant === 'resolve' ? Check : Undo2;

  return (
    <>
      <style>{`
        @keyframes comment-thread-action-rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(-12deg);
          }
        }
        .comment-thread-action-rotate:hover svg {
          animation: comment-thread-action-rotate 0.3s ease-in-out forwards;
        }
      `}</style>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {label ? (
              <Button
                type="button"
                size="sm"
                variant={variant === 'resolve' ? 'outline' : 'ghost'}
                disabled={disabled}
                className={
                  variant === 'resolve'
                    ? 'comment-thread-action-rotate hover:border-[var(--scribane-green)] hover:text-[var(--scribane-green)] dark:hover:border-[var(--scribane-green)] dark:hover:text-[var(--scribane-green)]'
                    : 'comment-thread-action-rotate hover:text-[var(--scribane-green)] dark:hover:text-[var(--scribane-green)]'
                }
                onClick={disabled ? undefined : onClick}
                aria-label={ariaLabel || tooltipText}
              >
                <Icon />
                {label}
              </Button>
            ) : (
              <button
                type="button"
                onClick={disabled ? undefined : onClick}
                disabled={disabled}
                className="comment-thread-action-rotate"
                style={{
                  padding: '0.5rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  color: 'var(--muted-foreground)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                  opacity: disabled ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!disabled) {
                    if (variant === 'resolve') {
                      e.currentTarget.style.backgroundColor = 'rgba(34, 197, 94, 0.14)';
                      e.currentTarget.style.color = 'rgb(22, 163, 74)';
                    } else {
                      e.currentTarget.style.backgroundColor = 'var(--muted)';
                      e.currentTarget.style.color = 'hsl(var(--primary))';
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  if (!disabled) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--muted-foreground)';
                  }
                }}
                aria-label={ariaLabel || tooltipText}
              >
                <Icon size={20} strokeWidth={2.25} />
              </button>
            )}
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </>
  );
};
